import { Loader } from 'components/Widget/Loader';
import { useEffect, useState } from 'react';
import { shallowEqual, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { selectOrganizations } from 'redux/db/reducers';
import { RootReducer } from 'redux/reducers';
import * as UserAccessStyles from '../UserAccessManager/styles';
import * as S from './styles';

interface OrganizationAccessManagerProps {
	entityId: string;
	getOrganizations: (
		id: string
	) => Promise<{ organizations: { organizationId: string; organizationName: string }[] }>;
	addToOrganization: (entityId: string, orgId: string) => Promise<void>;
	removeFromOrganization: (entityId: string, orgId: string) => Promise<void>;
	emptyStateMessage?: string;
	fetchFailedMessage?: string;
}

const OrganizationAccessManager = ({
	entityId,
	getOrganizations,
	addToOrganization,
	removeFromOrganization,
	emptyStateMessage = 'Not shared with any teams',
	fetchFailedMessage = 'Failed to load teams',
}: OrganizationAccessManagerProps) => {
	const { userOrganizations } = useSelector(
		(r: RootReducer) => ({
			userOrganizations: selectOrganizations(r.db),
		}),
		shallowEqual
	);

	const [entityOrganizations, setEntityOrganizations] = useState<
		{ organizationId: string; organizationName: string }[]
	>([]);
	const [isLoading, setIsLoading] = useState(true);
	const [addingOrgId, setAddingOrgId] = useState<string | null>(null);
	const [removingOrgId, setRemovingOrgId] = useState<string | null>(null);

	//----------------------------------------------------------------------
	// HOOKS
	//----------------------------------------------------------------------
	useEffect(() => {
		const fetchOrganizations = async () => {
			setIsLoading(true);
			try {
				const response = await getOrganizations(entityId);
				setEntityOrganizations(response.organizations);
			} catch (error) {
				toast.error(fetchFailedMessage);
			} finally {
				setIsLoading(false);
			}
		};

		fetchOrganizations();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [entityId]);

	//----------------------------------------------------------------------
	// HANDLERS
	//----------------------------------------------------------------------
	const onAddOrganization = async (organizationId: string) => {
		setAddingOrgId(organizationId);
		try {
			await addToOrganization(entityId, organizationId);
			const org = userOrganizations.find((o) => o.id === organizationId);
			if (org) {
				setEntityOrganizations((prev) => [
					...prev,
					{ organizationId: org.id, organizationName: org.name },
				]);
			}
		} catch (error) {
			console.error('Failed to add to organization:', error);
		} finally {
			setAddingOrgId(null);
		}
	};

	const onRemoveOrganization = async (organizationId: string) => {
		setRemovingOrgId(organizationId);
		try {
			await removeFromOrganization(entityId, organizationId);
			setEntityOrganizations((prev) => prev.filter((o) => o.organizationId !== organizationId));
		} catch (error) {
			console.error('Failed to remove from organization:', error);
		} finally {
			setRemovingOrgId(null);
		}
	};

	//----------------------------------------------------------------------
	// RENDERING
	//----------------------------------------------------------------------
	const hasEntityOrgs = entityOrganizations.length > 0;
	const entityOrgIds = new Set(entityOrganizations.map((o) => o.organizationId));
	const availableOrganizations = userOrganizations.filter((org) => !entityOrgIds.has(org.id));

	if (isLoading) {
		return (
			<>
				<UserAccessStyles.Divider />
				<S.LoadingContainer>
					<Loader width={32} height={32} />
					<S.LoadingText>Loading teams...</S.LoadingText>
				</S.LoadingContainer>
			</>
		);
	}

	// no user orgs and entity has no orgs — hide the entire section
	if (userOrganizations.length === 0 && !hasEntityOrgs) return null;

	return (
		<>
			<UserAccessStyles.Divider />
			{availableOrganizations.length > 0 && (
				<>
					<S.SectionTitle>Add to team</S.SectionTitle>
					<S.OrganizationList>
						{availableOrganizations.map((org) => (
							<S.OrganizationItem key={org.id}>
								<S.OrganizationName>{org.name}</S.OrganizationName>
								<S.AddButton
									onClick={() => onAddOrganization(org.id)}
									disabled={hasEntityOrgs || addingOrgId === org.id}
									title={hasEntityOrgs ? 'Already belongs to a team' : undefined}
								>
									{addingOrgId === org.id ? <Loader width={16} height={16} /> : 'Add'}
								</S.AddButton>
							</S.OrganizationItem>
						))}
					</S.OrganizationList>
					<S.Divider />
				</>
			)}
			<S.SectionTitle>Teams with access</S.SectionTitle>
			<S.OrganizationList>
				{!hasEntityOrgs ? (
					<S.EmptyState>{emptyStateMessage}</S.EmptyState>
				) : (
					entityOrganizations.map((org) => (
						<S.OrganizationItem key={org.organizationId}>
							<S.OrganizationName>{org.organizationName}</S.OrganizationName>
							<S.RemoveButton
								onClick={() => onRemoveOrganization(org.organizationId)}
								disabled={removingOrgId === org.organizationId}
							>
								{removingOrgId === org.organizationId ? (
									<Loader width={16} height={16} />
								) : (
									'Remove'
								)}
							</S.RemoveButton>
						</S.OrganizationItem>
					))
				)}
			</S.OrganizationList>
		</>
	);
};

export default OrganizationAccessManager;
